import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSession } from '../context/Session';
import { useT } from '../i18n';
import { ExerciseThumb, Icon, ImagePreview, Modal, SectionTitle, useCelebrate, useToast } from '../components/ui';
import { BADGE_CATALOG } from '../lib/badges';
import BadgeMedal from '../components/BadgeMedal';
import { computeStreakSnapshot } from '../lib/streak';
import { DAY_KEYS } from '../i18n';
import { isoWeekday, parseDateKey, addDays, startOfDay } from '../lib/dates';
import { useDoc, useExercises, useToday } from '../lib/hooks';
import { formatWeight, resolveExercise } from '../lib/program';
import { setExerciseCompleted, updateUser, workoutDayRef } from '../lib/services';
import { SHOW_BLOGS } from '../config';
import { useBodyDays, useEarnedBadges, useNutritionDays, useProgram, useProgressDays } from '../lib/useUserData';

const WORKOUT = 'var(--gm-workout)';
const NUTRITION = 'var(--gm-nutrition)';

/* ───────── Streaks ───────── */
export function DayDot({ color, done, frozen, isToday, applicable = true }) {
  if (!applicable) return <span className="gm-dot rest" />;
  const fill = done ? color : frozen ? `color-mix(in srgb, ${color} 35%, transparent)` : 'transparent';
  const border = isToday ? color : `color-mix(in srgb, ${color} 35%, transparent)`;
  return <span className="gm-dot" style={{ background: fill, borderColor: border, borderWidth: isToday ? 1.5 : 1 }} />;
}

export function StreaksCard({ snapshot }) {
  const t = useT();
  if (!snapshot) return null;
  const workoutSub = snapshot.todayWorkoutDone
    ? t('workoutStreakDoneToday')
    : snapshot.todayHasWorkout ? t('workoutStreakDoToday') : t('workoutStreakStart');
  const nutritionSub = snapshot.todayNutritionLogged ? t('nutritionStreakLoggedToday') : t('nutritionStreakLogToday');
  return (
    <section>
      <SectionTitle>{t('streaksSectionTitle')}</SectionTitle>
      <div className="gm-card gm-card--flush">
        <div className="gm-stripe"><span style={{ background: WORKOUT }} /><span style={{ background: NUTRITION }} /></div>
        <div style={{ padding: 16 }}>
          <div className="gm-streak-stats">
            <div>
              <div className="gm-streak-stat__value" style={{ color: WORKOUT }}>
                <span style={{ fontSize: 18 }}>🔥</span> {snapshot.programStreakDays} {t('daysUnit')}
              </div>
              <div className="gm-streak-stat__sub">{workoutSub}</div>
            </div>
            <div className="divider" />
            <div>
              <div className="gm-streak-stat__value" style={{ color: NUTRITION }}>
                <Icon name="restaurant_menu" size={18} /> {snapshot.nutritionStreakDays} {t('daysUnit')}
              </div>
              <div className="gm-streak-stat__sub">{nutritionSub}</div>
            </div>
          </div>
          <div className="gm-week-dots">
            {snapshot.week.map((d) => (
              <div key={d.dayKey} className={`day${d.isToday ? ' today' : ''}`}>
                <DayDot color={WORKOUT} done={d.workoutDone} frozen={d.workoutFrozen} isToday={d.isToday} applicable={d.hasWorkout} />
                <DayDot color={NUTRITION} done={d.nutritionLogged} frozen={d.nutritionFrozen} isToday={d.isToday} />
                <small>{t.dayShort(d.dayKey)}</small>
              </div>
            ))}
          </div>
          {snapshot.programFreezeAvailable && (
            <div className="gm-freeze" style={{ background: 'color-mix(in srgb, var(--gm-workout) 12%, transparent)' }}>
              <span>🧊</span>
              <div className="gm-grow">
                <div className="gm-strong">{t('streakFreezeProgramTitle', snapshot.programFreezeRecoverableDays)}</div>
                <div className="gm-muted gm-tiny">{t('streakFreezeSubtitle', snapshot.programFreezesRemaining)} · {t('webOnlyMobileFeature')}</div>
              </div>
            </div>
          )}
          {snapshot.nutritionFreezeAvailable && (
            <div className="gm-freeze" style={{ background: 'color-mix(in srgb, var(--gm-nutrition) 12%, transparent)' }}>
              <span>🧊</span>
              <div className="gm-grow">
                <div className="gm-strong">{t('streakFreezeNutritionTitle', snapshot.nutritionFreezeRecoverableDays)}</div>
                <div className="gm-muted gm-tiny">{t('streakFreezeSubtitle', snapshot.nutritionFreezesRemaining)} · {t('webOnlyMobileFeature')}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ───────── Badges ───────── */
// Mirrors the app's BadgesHomeCarousel: compact medal tiles, earned first
// (most recent first), then the nearest locked ones. Awarding and celebrating
// live in WebApp's BadgeCelebrations.
function BadgesStrip({ uid, earned, counts }) {
  const t = useT();
  const navigate = useNavigate();
  const earnedList = BADGE_CATALOG.filter((b) => b.id in earned).sort((a, b) => (earned[b.id] || 0) - (earned[a.id] || 0));
  const locked = BADGE_CATALOG.filter((b) => !(b.id in earned));
  const tiles = [...earnedList, ...locked].slice(0, 5);
  return (
    <section>
      <SectionTitle right={<Link to="/badges" className="gm-link">{t('badgesSeeAllLink')}</Link>}>{t('badgesSectionTitle')}</SectionTitle>
      <div className="gm-badge-row">
        {tiles.map((b) => {
          const isEarned = b.id in earned;
          return (
            <button
              key={b.id}
              type="button"
              className={`gm-badge gm-badge--mini${isEarned ? ' earned' : ''}`}
              style={{ '--badge-color': b.color }}
              title={t.badgeDescription(b.id)}
              onClick={() => navigate('/badges')}
            >
              <BadgeMedal badge={b} earned={isEarned} size={42} count={counts[b.id] || 0} />
              <div className="gm-badge__title">{t.badgeTitle(b.id)}</div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

/* ───────── Today's exercises ───────── */
export function exerciseMetaLine(t, e, durationFirst) {
  const parts = [];
  const hasDur = e.durationMin && e.durationMin > 0;
  if (!(durationFirst && hasDur)) parts.push(`${e.sets} × ${e.reps}`);
  if (hasDur) parts.push(`${e.durationMin} ${t('programExerciseDurationMinSuffix')}`);
  if (e.weight != null) parts.push(`${formatWeight(e.weight)} kg`);
  return parts.join(' · ');
}

function TodaysExercises({ user, program, today, onCompleted }) {
  const t = useT();
  const toast = useToast();
  const { byId } = useExercises();
  const [preview, setPreview] = useState(null);
  const dayKey = DAY_KEYS[isoWeekday(today) - 1];
  const entries = (program && program[dayKey]) || [];
  const { data: progress } = useDoc(workoutDayRef(user.id, today));
  // Local overrides until Firestore confirms them. An override is dropped only once the
  // server value matches it — clearing on *any* snapshot made ticks flip back and forth
  // when a snapshot landed before the transaction committed.
  const [optimistic, setOptimistic] = useState({});
  useEffect(() => {
    setOptimistic((o) => {
      const n = { ...o };
      let changed = false;
      Object.keys(n).forEach((id) => {
        if ((progress?.exercises?.[id]?.completed === true) === n[id]) {
          delete n[id];
          changed = true;
        }
      });
      return changed ? n : o;
    });
  }, [progress]);
  // Taps run one transaction at a time, in order, so rapid toggles can't race.
  const queue = useRef(Promise.resolve());

  const isDone = (id) => (id in optimistic ? optimistic[id] : progress?.exercises?.[id]?.completed === true);
  const allIds = entries.map((e) => e.exerciseId);

  const toggle = (id) => {
    const next = !isDone(id);
    setOptimistic((o) => ({ ...o, [id]: next }));
    queue.current = queue.current.then(async () => {
      try {
        const justCompleted = await setExerciseCompleted({ uid: user.id, date: today, programId: user.exerciseProgramID, exerciseId: id, completed: next, allExerciseIds: allIds });
        if (justCompleted) onCompleted?.();
      } catch (e) {
        setOptimistic((o) => {
          const n = { ...o };
          delete n[id];
          return n;
        });
        toast.error(t('progressSaveFailed'));
      }
    });
  };

  const doneCount = allIds.filter(isDone).length;

  return (
    <section>
      <SectionTitle right={entries.length > 0 && <span className="gm-pill">{doneCount}/{entries.length}</span>}>{t('todaysExercises')}</SectionTitle>
      <div className="gm-card">
        {entries.length === 0 ? (
          <div className="gm-row" style={{ padding: '4px 0' }}>
            <span style={{ fontSize: 26 }}>😴</span>
            <span className="gm-muted">{t('noExercisesForToday')}</span>
          </div>
        ) : (
          entries.map((e) => {
            const ex = resolveExercise(byId, e);
            const done = isDone(e.exerciseId);
            return (
              <div key={e.exerciseId} className={`gm-ex-row${done ? ' done' : ''}`}>
                <ExerciseThumb exercise={ex} onClick={() => ex && setPreview(ex)} />
                <div className="gm-grow">
                  <div className="gm-ex-name">{ex?.name || e.exerciseId}</div>
                  <div className="gm-ex-meta">{exerciseMetaLine(t, e, user.programDurationInsteadOfSetsReps)}</div>
                </div>
                <button type="button" className={`gm-tick${done ? ' on' : ''}`} onClick={() => toggle(e.exerciseId)} aria-pressed={done} aria-label={t('completed')}>
                  <Icon name="check" />
                </button>
              </div>
            );
          })
        )}
      </div>
      <ImagePreview exercise={preview} onClose={() => setPreview(null)} />
    </section>
  );
}

/* ───────── Weekly summary card (Mondays) ───────── */
function WeeklySummaryCard({ today }) {
  const t = useT();
  const navigate = useNavigate();
  if (isoWeekday(today) !== 1) return null;
  return (
    <div className="gm-card gm-card--click gm-action-card" onClick={() => navigate('/weekly-summary')}>
      <div className="gm-icon-tile"><Icon name="auto_awesome" /></div>
      <div className="gm-grow">
        <div className="gm-card-title">{t('weeklySummaryCardTitle')}</div>
        <div className="gm-muted" style={{ marginTop: 4 }}>{t('weeklySummaryCardDescription')}</div>
      </div>
      <Icon name="arrow_forward_ios" size={16} className="gm-muted" />
    </div>
  );
}

/* ───────── Mini progress ───────── */
function changeInWindow(items, field, cutoff) {
  const list = items.filter((e) => e[field] != null && parseDateKey(e.id));
  if (!list.length) return { last: null, change: null };
  const inWindow = list.filter((e) => parseDateKey(e.id) >= cutoff);
  if (!inWindow.length) return { last: list[list.length - 1][field], change: null };
  const last = inWindow[inWindow.length - 1][field];
  return { last, change: inWindow.length > 1 ? last - inWindow[0][field] : null };
}

function ProgressBox({ label, value, change, unit }) {
  const t = useT();
  const color = change == null ? 'var(--gm-muted)' : change > 0 ? 'var(--gm-error)' : change < 0 ? 'var(--gm-success)' : 'var(--gm-muted)';
  return (
    <div className="gm-card" style={{ padding: '14px 12px' }}>
      <div className="gm-label">{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{value != null ? `${value.toFixed(1)} ${unit}` : '—'}</div>
      {change != null && (
        <div className="gm-tiny" style={{ color, marginTop: 6 }}>
          {t('lastWeekLabel')}: {change >= 0 ? '+' : ''}{change.toFixed(1)} {unit}
        </div>
      )}
    </div>
  );
}

function MiniProgress({ bodyDays, today }) {
  const t = useT();
  const cutoff = addDays(startOfDay(today), -7);
  const w = changeInWindow(bodyDays, 'weight_kg', cutoff);
  const f = changeInWindow(bodyDays, 'body_fat_percent', cutoff);
  return (
    <section>
      <SectionTitle right={<Link to="/progress" className="gm-link">{t('progress')}</Link>}>{t('miniProgressTitle')}</SectionTitle>
      <div className="gm-grid-2 keep">
        <ProgressBox label={t.upper(t('weight'))} value={w.last} change={w.change} unit="kg" />
        <ProgressBox label={t.upper(t('bodyFat'))} value={f.last} change={f.change} unit="%" />
      </div>
    </section>
  );
}

/* ───────── Starter todos ───────── */
function StarterTodos({ user }) {
  const t = useT();
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(false);
  const [widgetInfo, setWidgetInfo] = useState(false);
  useEffect(() => {
    if (user.starterTodosEnabled) setHidden(false);
  }, [user.starterTodosEnabled]);
  if (hidden || !user.starterTodosEnabled) return null;
  const items = [
    { label: t('homeStarterTodoCreateProgram'), done: !!user.starterTodoCreateProgram, go: () => navigate(user.isTrainer ? '/home' : '/ai-trainer') },
    { label: t('homeStarterTodoLogFirstMeal'), done: !!user.starterTodoLogFirstMeal, go: () => navigate('/nutrition') },
    { label: t('homeStarterTodoCustomizeProgram'), done: !!user.starterTodoCustomizeProgram, go: () => navigate('/program') },
    { label: t('homeStarterTodoAddStudent'), done: !!user.starterTodoAddStudent, go: () => navigate(user.isTrainer ? '/trainees' : '/profile') },
    { label: t('homeStarterTodoAddWidget'), done: !!user.starterTodoAddWidget, go: () => setWidgetInfo(true) },
  ];
  if (items.every((i) => i.done)) return null;
  const dismiss = () => {
    setHidden(true);
    updateUser(user.id, { starterTodosEnabled: false }).catch(() => {});
  };
  return (
    <div className="gm-card gm-card--pad-lg">
      <div className="gm-card-title" style={{ marginBottom: 12 }}>{t('homeStarterTodosTitle')}</div>
      <div className="gm-stack-sm">
        {items.map((i) => (
          <div key={i.label} className={`gm-todo${i.done ? ' done' : ' clickable'}`} onClick={i.done ? undefined : i.go}>
            <span className="gm-todo__check">{i.done && <Icon name="check" size={14} />}</span>
            <span className="gm-todo__label">{i.label}</span>
            {!i.done && <Icon name="chevron_right" size={18} className="gm-muted" />}
          </div>
        ))}
      </div>
      <div style={{ marginTop: 14 }}>
        <button type="button" className="gm-link" onClick={dismiss}>{t('homeStarterTodosCloseLink')}</button>
      </div>
      <Modal open={widgetInfo} onClose={() => setWidgetInfo(false)} title={t('homeStarterTodoAddWidgetDialogTitle')} footer={<button type="button" className="gm-btn" onClick={() => setWidgetInfo(false)}>{t('ok')}</button>}>
        <p className="gm-muted">{t('homeStarterTodoAddWidgetDialogBody')}</p>
        <p className="gm-small" style={{ marginTop: 10 }}>{t('homeWidgetWebHint')}</p>
      </Modal>
    </div>
  );
}

function ActionCard({ icon, title, description, onClick }) {
  return (
    <div className="gm-card gm-card--click gm-action-card" onClick={onClick}>
      <div className="gm-icon-tile"><Icon name={icon} /></div>
      <div className="gm-grow">
        <div className="gm-card-title">{title}</div>
        <div className="gm-muted" style={{ marginTop: 4 }}>{description}</div>
      </div>
      <Icon name="arrow_forward_ios" size={16} className="gm-muted" />
    </div>
  );
}

/* ───────── Page ───────── */
function TraineeHome({ user }) {
  const t = useT();
  const today = useToday();
  const { program } = useProgram(user.exerciseProgramID);
  const { progressDays } = useProgressDays(user.id);
  const { nutritionDays } = useNutritionDays(user.id);
  const { bodyDays } = useBodyDays(user.id);
  const { earned, counts } = useEarnedBadges(user.id);
  const celebrate = useCelebrate();

  const snapshot = useMemo(
    () => computeStreakSnapshot({ progressDays, program, nutritionDays, userData: user, today }),
    [progressDays, program, nutritionDays, user, today],
  );
  const hasProgram = !!user.exerciseProgramID;

  return (
    <div className="gm-content gm-enter">
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800 }}>{t('welcome', user.firstName || '')}</h1>
        <div className="gm-muted gm-small" style={{ marginTop: 2 }}>{today.toLocaleDateString(t.locale, { weekday: 'long', day: 'numeric', month: 'long' })}</div>
      </div>
      <div className="gm-dash">
        <div className="gm-stack">
          {hasProgram && <StreaksCard snapshot={snapshot} />}
          {hasProgram && <TodaysExercises user={user} program={program} today={today} onCompleted={() => celebrate({ emoji: '🔥', message: t('streakGreatJobKeepGoing') })} />}
          {!hasProgram && <StarterTodos user={user} />}
        </div>
        <div className="gm-stack">
          {hasProgram && <StarterTodos user={user} />}
          {hasProgram && <BadgesStrip uid={user.id} earned={earned} counts={counts} />}
          <WeeklySummaryCard today={today} />
          {hasProgram && <MiniProgress bodyDays={bodyDays} today={today} />}
        </div>
      </div>
    </div>
  );
}

function TrainerHome({ user }) {
  const t = useT();
  const navigate = useNavigate();
  return (
    <div className="gm-content gm-content--narrow gm-enter">
      <h1 style={{ fontSize: 22, fontWeight: 800 }}>{t('welcome', user.firstName || '')}</h1>
      <div className="gm-muted gm-small" style={{ marginTop: 2, marginBottom: 18 }}>{t.upper(t('trainerDashboard'))}</div>
      <div className="gm-stack">
        <StarterTodos user={user} />
        <ActionCard icon="people" title={t('manageTrainees')} description={t('viewAndManageTrainees')} onClick={() => navigate('/trainees')} />
        {SHOW_BLOGS && (
        <ActionCard
          icon="article"
          title={t('learnTrainerTraineeConnection')}
          description={t('learnTrainerTraineeConnectionDescription')}
          onClick={() => navigate('/blogs/gymgm-trainer-trainee-connection')}
        />
        )}
      </div>
    </div>
  );
}

export default function Home() {
  const { user } = useSession();
  return user.isTrainer ? <TrainerHome user={user} /> : <TraineeHome user={user} />;
}

export function allStarterTodosDone(user) {
  return !!(user.starterTodoCreateProgram && user.starterTodoCustomizeProgram && user.starterTodoLogFirstMeal && user.starterTodoAddStudent && user.starterTodoAddWidget);
}
